import {useMediaQuery} from '@mui/material'
import {FC, MouseEventHandler, ReactNode, useState} from 'react'
import {RegisterButton} from 'features/buttons'
import {text} from 'shared/mirrors/text'
import styles from './styles.module.scss'

const switcherValues = ['Reply rate', 'Matches']
const chartValues = {
  other: [
    {value: 27.6, height: 67},
    {value: 60.3, height: 145}
  ],
  our: [
    {value: 78, height: 187},
    {value: 81, height: 194}
  ]
}

const leftAnimationStep = 0.5
const rightAnimationStep = 0.1

export const SourceSection: FC = () => {
  const [isMatches, setIsMatches] = useState(false)
  const [isDisabled, setIsDisabled] = useState(false)
  const [currentChartValues, setCurrentChartValues] = useState({
    left: chartValues.other[0].value,
    right: chartValues.our[0].value
  })

  const is1440p = useMediaQuery(`(max-width: ${styles.is1440p})`)

  const onClickHandler = () => {
    if (isDisabled) return

    const {other, our} = chartValues
    setIsDisabled(true)
    setIsMatches(!isMatches)

    const interval = setInterval(() => {
      setCurrentChartValues(({left, right}) => {
        if (!isMatches ? left >= other[1].value : left <= other[0].value) {
          clearInterval(interval)

          setCurrentChartValues({
            left: !isMatches ? other[1].value : other[0].value,
            right: !isMatches ? our[1].value : our[0].value
          })

          setIsDisabled(false)
        }

        return ({
          left: +(!isMatches ? left + leftAnimationStep : left - leftAnimationStep).toFixed(1),
          right: +(!isMatches ? (right + leftAnimationStep >= our[1].value ?
            our[1].value : right + rightAnimationStep) : (right - leftAnimationStep <= our[0].value ?
            our[0].value : right - rightAnimationStep)).toFixed(1)
        })
      })
    }, 1)

    return () => clearInterval(interval)
  }

  return (
    <div className={styles.container}>
      <div className={styles.sourceWrapper}>
        <div className={styles.sourceBox}>
          <div className={`${styles.box} ${is1440p ? styles.transparentBorder : ''}`}>
            <Title />
            <PercentBlock value={64}>
              Of users <span className={styles.bold}>find it easy</span> or somewhat easy to find people who share
              interests and hobbies
            </PercentBlock>
            <PercentBlock value={66}>
              Of online daters have been on at least <span className={styles.bold}>one date</span> with someone they
              were matched with
            </PercentBlock>
          </div>
          <span className={styles.source}>Source: Pew Research Center</span>
        </div>
        <div className={styles.sourceBox}>
          <div className={styles.boxChart}>
            <p className={styles.chartTitle}>
              Reply rate vs matches: {text.main.siteName} {!is1440p && <br />}and other resources
            </p>
            <SwitcherComponent state={isMatches} onClick={onClickHandler} />
            <div className={styles.chartContainer}>
              <Chart
                value={currentChartValues.left}
                description='Other site'
                height={!isMatches ? chartValues.other[0].height : chartValues.other[1].height}
              />
              <Chart
                value={currentChartValues.right}
                description={text.main.siteName}
                height={!isMatches ? chartValues.our[0].height : chartValues.our[1].height}
                isOur
              />
            </div>
          </div>
          <span className={styles.source}>Source: Thesocietypages research</span>
        </div>
      </div>
      <RegisterButton className={styles.btn} styleType='primary'>Get started</RegisterButton>
    </div>
  )
}

const Title: FC = () =>
  <h2 className={styles.title}>We help <span className={styles.highlight}>bring people</span> together</h2>

type PercentBlockProps = {
  value: number,
  children: ReactNode
}

const PercentBlock: FC<PercentBlockProps> = ({value, children}) =>
  <div className={styles.percent}>
    <p className={`${styles.highlight} ${styles.percent__value}`}>{value}%</p>
    <p className={styles.description}>{children}</p>
  </div>

type SwitcherComponentProps = {
  state: boolean,
  onClick: MouseEventHandler<HTMLDivElement>
}

const SwitcherComponent: FC<SwitcherComponentProps> = ({state, onClick}) =>
  <div onClick={onClick} className={styles.switcher}>
    <div className={`${styles.switcher__toggle} ${state ? styles.switcher__toggle_active : ''}`}>
      {!state ? switcherValues[0] : switcherValues[1]}
    </div>
    <span>{switcherValues[0]}</span>
    <span>{switcherValues[1]}</span>
  </div>


type ChartProps = {
  description: string,
  value: number,
  height: number,
  isOur?: boolean
}

const Chart: FC<ChartProps> = ({description, value, height, isOur}) => {
  return (
    <div className={styles.chartWrapper}>
      <div
        style={{
          height,
          background: isOur ? 'linear-gradient(0deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.2)), rgba(63, 105, 255, 0.8)' : '#A3ADBA'
        }}
        className={styles.chart}
      >
        {value}%
      </div>
      <p className={styles.chart__description}>{description}</p>
    </div>
  )
}